if (!customElements.get('gift-wrap-option')) {
  customElements.define(
    'gift-wrap-option',
    class GiftWrapOption extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.productForm = this.closest('product-form');
        this.form = this.productForm?.form;
        this.checkbox = this.querySelector('.gift-wrap-option__checkbox');
        this.infoButton = this.querySelector('.gift-wrap-info__trigger');
        this.infoTooltip = this.querySelector('.gift-wrap-info__tooltip');
        this.submitButton = this.form?.querySelector('[name="add"]');

        if (!this.form || !this.checkbox) return;

        this.handleSubmit = this.onSubmit.bind(this);
        this.handleInfoClick = this.onInfoClick.bind(this);
        this.handleDocumentClick = this.onDocumentClick.bind(this);
        this.handleEscape = this.onEscape.bind(this);

        this.form.addEventListener('submit', this.handleSubmit, true);
        this.infoButton?.addEventListener('click', this.handleInfoClick);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleEscape);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmit, true);
        this.infoButton?.removeEventListener('click', this.handleInfoClick);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleEscape);
      }

      onInfoClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const isOpen = this.dataset.infoOpen === 'true';
        this.dataset.infoOpen = isOpen ? 'false' : 'true';
        this.infoButton?.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.closeInfo();
        }
      }

      onEscape(event) {
        if (event.key === 'Escape') {
          this.closeInfo();
          this.infoButton?.focus();
        }
      }

      closeInfo() {
        this.dataset.infoOpen = 'false';
        this.infoButton?.setAttribute('aria-expanded', 'false');
      }

      async onSubmit(event) {
        if (!this.checkbox.checked) return;
        if (this.isSubmitting) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const variantInput = this.form.querySelector('[name="id"]');
        const mainVariantId = Number(variantInput?.value);
        const giftWrapVariantId = Number(this.dataset.giftWrapVariantId);
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10));
        const cart = this.productForm?.cart;

        if (!mainVariantId || !giftWrapVariantId) {
          this.productForm?.handleErrorMessage?.('Ambalarea cadou nu este disponibila momentan.');
          return;
        }

        this.productForm?.handleErrorMessage?.();
        this.setLoading(true);

        let addSucceeded = false;

        try {
          const payload = {
            items: [
              this.buildMainItem(mainVariantId, quantity),
              {
                id: giftWrapVariantId,
                quantity,
                parent_id: mainVariantId,
                properties: {
                  _gift_wrap: 'true',
                },
              },
            ],
          };

          if (cart?.getSectionsToRender) {
            payload.sections = cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            cart.setActiveElement?.(document.activeElement);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(payload);

          const response = await fetch(`${routes.cart_add_url}`, config);
          const parsedState = await response.json();

          if (!response.ok || parsedState.status || parsedState.errors) {
            const message =
              parsedState.description ||
              parsedState.message ||
              parsedState.errors ||
              'Nu am putut adauga produsul cu ambalare cadou.';

            this.productForm?.handleErrorMessage?.(
              typeof message === 'string' ? message : 'Nu am putut adauga produsul cu ambalare cadou.',
            );

            if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartError) {
              Promise.resolve(
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'gift-wrap-option',
                  productVariantId: String(mainVariantId),
                  errors: message,
                  message,
                }),
              ).catch((error) => console.warn('[Aeris gift wrap] Cart error event failed', error));
            }
            return;
          }

          addSucceeded = true;
          this.checkbox.checked = false;

          if (!cart?.renderContents) {
            window.location = window.routes.cart_url;
            return;
          }

          try {
            this.renderCartUi(cart, parsedState, mainVariantId);
          } catch (uiError) {
            console.warn('[Aeris gift wrap] Prima randare a notificarii a esuat. Se incearca reimprospatarea sectiunilor.', uiError);
            await this.refreshCartUi(cart, parsedState, mainVariantId);
          }

          this.publishCartUpdate(mainVariantId);
        } catch (error) {
          console.error('[Aeris gift wrap]', error);

          if (!addSucceeded) {
            this.productForm?.handleErrorMessage?.('A aparut o eroare. Te rugam sa incerci din nou.');
          }
        } finally {
          this.setLoading(false);
        }
      }

      normalizeCartState(parsedState, mainVariantId) {
        const items = Array.isArray(parsedState?.items) ? parsedState.items : [parsedState];
        const mainItem = items.find((item) => {
          const variantId = Number(item?.variant_id ?? item?.id);
          return variantId === mainVariantId && item?.properties?._gift_wrap !== 'true';
        }) || items[0];

        return {
          ...parsedState,
          id: parsedState?.id || mainVariantId,
          key: parsedState?.key || mainItem?.key,
        };
      }

      renderCartUi(cart, parsedState, mainVariantId) {
        const state = this.normalizeCartState(parsedState, mainVariantId);

        if (!state.sections) {
          throw new Error('Raspunsul Shopify nu contine sectiunile cosului.');
        }

        if (cart.tagName === 'CART-NOTIFICATION' && !state.key) {
          throw new Error('Nu a fost gasita cheia produsului principal pentru notificare.');
        }

        cart.classList?.remove('is-empty');
        cart.renderContents(state);
      }

      async refreshCartUi(cart, parsedState, mainVariantId) {
        const sectionIds = cart.getSectionsToRender?.().map((section) => section.id) || [];
        if (!sectionIds.length) return;

        const separator = window.location.search ? '&' : '?';
        const sectionUrl = `${window.location.pathname}${window.location.search}${separator}sections=${sectionIds.join(',')}`;
        const response = await fetch(sectionUrl, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error('Nu am putut actualiza continutul cosului.');
        }

        const sections = await response.json();
        const missingSection = sectionIds.find((sectionId) => typeof sections?.[sectionId] !== 'string');

        if (missingSection) {
          throw new Error(`Lipseste sectiunea cosului: ${missingSection}`);
        }

        this.renderCartUi(cart, { ...parsedState, sections }, mainVariantId);
      }

      publishCartUpdate(mainVariantId) {
        if (typeof publish !== 'function' || !window.PUB_SUB_EVENTS?.cartUpdate) return;

        fetch(`${routes.cart_url}.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        })
          .then((response) => {
            if (!response.ok) throw new Error('Nu am putut citi cosul actualizat.');
            return response.json();
          })
          .then((cartData) =>
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'gift-wrap-option',
              productVariantId: String(mainVariantId),
              cartData,
            }),
          )
          .catch((error) => {
            console.warn('[Aeris gift wrap] Cart update event failed', error);
          });
      }

      buildMainItem(variantId, quantity) {
        const item = {
          id: variantId,
          quantity,
        };

        const formData = new FormData(this.form);
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        }

        if (Object.keys(properties).length) item.properties = properties;

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number(sellingPlan);

        return item;
      }

      setLoading(isLoading) {
        this.isSubmitting = isLoading;
        this.checkbox.disabled = isLoading;

        if (!this.submitButton) return;

        this.submitButton.toggleAttribute('aria-disabled', isLoading);
        this.submitButton.classList.toggle('loading', isLoading);
        const spinner = this.submitButton.querySelector('.loading__spinner');
        spinner?.classList.toggle('hidden', !isLoading);
      }
    },
  );
}
